// Pure trend-calculation helpers for patient baseline charts. No React,
// no fetching — just history array in, chart-ready data out.

/**
 * A baseline reading counts as "no data" for trend purposes when it's
 * null/undefined, an empty string, or exactly 0 — none of these fields
 * (weight, BP, pulse, waist, cholesterol) are ever legitimately zero,
 * so 0 only ever means unset/default, never a real reading.
 */
const isMeaningfulReading = (value) =>
  value !== null && value !== undefined && value !== "" && Number(value) !== 0;

/**
 * Baseline history (any order) into ascending, Highcharts-ready [x, y]
 * points for one numeric field. Entries with no meaningful reading for
 * that field are dropped entirely rather than plotted as zero, so a
 * missing/blank reading never shows up as a dip in the trend line.
 *
 * Plotted against `created` (the server-stamped save time), not
 * `baselineDate` — no form on the frontend actually captures baselineDate,
 * so it's always null on every record; `created` is the one date the
 * backend guarantees is always populated.
 */
export const toTrendPoints = (history, getValue) =>
  (history ?? [])
    .filter((baseline) => isMeaningfulReading(getValue(baseline)))
    .map((baseline) => [new Date(baseline.created).getTime(), Number(getValue(baseline))])
    .sort((a, b) => a[0] - b[0]);

/**
 * Compares the latest meaningful reading against the most recent PRIOR
 * meaningful reading for a field. Because toTrendPoints() already drops
 * 0/null/empty entries, this naturally walks back past any gaps — a 0
 * or missing reading in between never becomes "the previous" value.
 * Returns null when there's no prior point to compare against. Includes
 * both points' dates so a widget can show exactly which two readings are
 * being compared, not just the delta between them.
 */
export const getTrendIndicator = (history, getValue) => {
  const points = toTrendPoints(history, getValue);
  if (points.length < 2) return null;

  const [previousDate, previous] = points[points.length - 2];
  const [currentDate, current] = points[points.length - 1];
  const delta = current - previous;

  return {
    current,
    previous,
    currentDate,
    previousDate,
    delta,
    direction: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
  };
};

/** One chart per metric; multi-field metrics (e.g. BP) render as multiple series on one chart. */
export const BASELINE_METRICS = [
  {
    key: "weight",
    title: "Weight",
    unit: "kg",
    fields: [{ name: "Weight", getValue: (baseline) => baseline.weightKg, color: "#059669" }],
  },
  {
    key: "bloodPressure",
    title: "Blood Pressure",
    unit: "mmHg",
    fields: [
      { name: "Systolic", getValue: (baseline) => baseline.systolic, color: "#dc2626" },
      { name: "Diastolic", getValue: (baseline) => baseline.diastolic, color: "#2563eb" },
    ],
  },
  {
    key: "pulse",
    title: "Pulse",
    unit: "bpm",
    fields: [{ name: "Pulse", getValue: (baseline) => baseline.pulse, color: "#7c3aed" }],
  },
  {
    key: "waist",
    title: "Waist Circumference",
    unit: "cm",
    fields: [{ name: "Waist", getValue: (baseline) => baseline.waistCm, color: "#d97706" }],
  }
];

export const buildSeriesForMetric = (metric, history) =>
  metric.fields.map((field) => ({
    name: field.name,
    color: field.color,
    data: toTrendPoints(history, field.getValue),
  }));

/** Every widget shows a "vs previous" indicator, computed from its primary (first) field. */
export const getMetricIndicator = (metric, history) =>
  getTrendIndicator(history, metric.fields[0].getValue);
